import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {DunningData} from '../model/dunning-data.interface';
import {PaymentPlan} from '../model/payment-plan.interface';
import {PaymentPlanResult} from '../model/payment-plan-result.interface';
import {Contract} from '../model/contract.interface';
import {ContractActivity} from '../model/contract-activity.interface';
import {Charge} from '../model/charge.interface';
import {ChargeAdjustment} from '../model/charge-adjustment.interface';
import {OneTimePayment} from '../model/one-time-payment.interface';
import {BalanceEntry} from '../model/balance-entry.interface';

@Injectable({
    providedIn: 'root'
})
export class BmPaymentPlanService {

    // Subjects for reactive updates
    private dunningDataSubject = new BehaviorSubject<DunningData | null>(null);
    private paymentPlanSubject = new BehaviorSubject<PaymentPlan | null>(null);
    private balanceEntriesSubject = new BehaviorSubject<BalanceEntry[]>([]);
    private paymentPlanResultSubject = new BehaviorSubject<PaymentPlanResult | null>(null);
    private chargeAdjustmentsSubject = new BehaviorSubject<ChargeAdjustment[]>([]);
    private selectedContractSubject = new BehaviorSubject<Contract | null>(null);

    // Observables for components to subscribe to
    public dunningData$: Observable<DunningData | null> = this.dunningDataSubject.asObservable();
    public paymentPlan$: Observable<PaymentPlan | null> = this.paymentPlanSubject.asObservable();
    public balanceEntries$: Observable<BalanceEntry[]> = this.balanceEntriesSubject.asObservable();
    public paymentPlanResult$: Observable<PaymentPlanResult | null> = this.paymentPlanResultSubject.asObservable();
    public chargeAdjustments$: Observable<ChargeAdjustment[]> = this.chargeAdjustmentsSubject.asObservable();
    selectedContract$ = this.selectedContractSubject.asObservable();

    constructor() {
    }

    /**
     * Initialize the payment plan with dunning data
     * @param dunningId The dunning ID to load data for
     */
    async initializePaymentPlan(dunningId: number): Promise<void> {
        try {
            const dunningData = await this.loadDunningData(dunningId);
            this.dunningDataSubject.next(dunningData);

            // Initialize payment plan with initial charges
            await this.initializeBalanceWithCharges(dunningData);

            // Initialize empty payment plan
            const initialPaymentPlan: PaymentPlan = {
                contracts: [],
                oneTimePayments: [],
                totalOwedAmount: 0,
                payBackAmountPerMonth: 0
            };
            this.paymentPlanSubject.next(initialPaymentPlan);

            // Calculate initial totals
            await this.recalculate();
        } catch (error) {
            console.error('Error initializing payment plan:', error);
            throw error;
        }
    }

    /**
     * Load dunning data from API (to be implemented)
     * For now, this is a placeholder - will be overridden by mock service
     */
    async loadDunningData(dunningId: number): Promise<DunningData> {
        // This will be implemented to call the actual API
        // For testing, the mock service will override this
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    customerId: 0,
                    customerBillingAccountId: 0,
                    dunningId: dunningId,
                    contracts: [],
                    charges: []
                });
            }, 100);
        });
    }

    /**
     * Initialize balance entries with charges from dunning data
     */
    private async initializeBalanceWithCharges(dunningData: DunningData): Promise<void> {
        const entries: BalanceEntry[] = dunningData.charges.map((charge, index) => ({
            id: `charge-${index}-${Date.now()}`,
            type: 'charge',
            name: charge.name,
            amount: charge.amount,
            currency: charge.currency,
            timestamp: new Date(),
            removable: charge.deductable
        }));

        this.balanceEntriesSubject.next(entries);
    }

    /**
     * Add or update a contract activity (reactivate or will-cancel)
     */
    async setContractActivity(contract: Contract, activity: 'reactivate' | 'will-cancel'): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan) return;

        // Remove existing activity for this contract
        const filteredActivities = currentPlan.contracts.filter(
            ca => ca.contract.identifier !== contract.identifier
        );

        // Calculate cost based on activity type
        const costAmount = activity === 'reactivate' ? contract.reactivationAmount : contract.dunningAmount;

        // Create new contract activity
        const newActivity: ContractActivity = {
            contract: contract,
            activity: activity,
            costAmount: costAmount,
            currency: contract.currency
        };

        // Update payment plan
        const updatedPlan: PaymentPlan = {
            ...currentPlan,
            contracts: [...filteredActivities, newActivity]
        };

        this.paymentPlanSubject.next(updatedPlan);

        // Update balance entries
        await this.updateBalanceForContractActivity(contract, activity, costAmount);

        // Recalculate totals
        await this.recalculate();
    }

    /**
     * Remove contract activity
     */
    async removeContractActivity(contractIdentifier: string): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan) return;

        // Remove contract activity
        const updatedPlan: PaymentPlan = {
            ...currentPlan,
            contracts: currentPlan.contracts.filter(
                ca => ca.contract.identifier !== contractIdentifier
            )
        };

        this.paymentPlanSubject.next(updatedPlan);

        // Remove from balance
        await this.removeBalanceEntry(`contract-${contractIdentifier}`);

        // Recalculate totals
        await this.recalculate();
    }

    /**
     * Update balance entries for contract activity
     */
    private async updateBalanceForContractActivity(
        contract: Contract,
        activity: 'reactivate' | 'will-cancel',
        costAmount: number
    ): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;

        // Remove existing entry for this contract
        const filteredEntries = currentEntries.filter(
            entry => entry.id !== `contract-${contract.identifier}`
        );

        // Create new balance entry
        const newEntry: BalanceEntry = {
            id: `contract-${contract.identifier}`,
            type: activity === 'reactivate' ? 'contract-reactivation' : 'contract-dunning',
            name: `${contract.type} - ${contract.partner} (${contract.identifier})`,
            amount: costAmount,
            currency: contract.currency,
            timestamp: new Date(),
            removable: true
        };

        this.balanceEntriesSubject.next([...filteredEntries, newEntry]);
    }

    /**
     * Adjust a charge (remove or reduce)
     */
    async adjustCharge(charge: Charge, adjustmentType: 'remove' | 'reduce', newAmount?: number): Promise<void> {
        const currentAdjustments = this.chargeAdjustmentsSubject.value;

        // Remove existing adjustment for this charge
        const filteredAdjustments = currentAdjustments.filter(
            adj => adj.charge.name !== charge.name
        );

        if (adjustmentType === 'remove') {
            // Create removal adjustment
            const adjustment: ChargeAdjustment = {
                charge: charge,
                adjustmentType: 'remove',
                newAmount: 0,
                currency: charge.currency
            };

            this.chargeAdjustmentsSubject.next([...filteredAdjustments, adjustment]);

            // Remove from balance
            await this.removeBalanceEntryByName(charge.name);

        } else if (adjustmentType === 'reduce' && newAmount !== undefined) {

            const adjustment: ChargeAdjustment = {
                charge,
                adjustmentType: 'reduce',
                newAmount,
                currency: charge.currency
            };

            this.chargeAdjustmentsSubject.next([...filteredAdjustments, adjustment]);

            const currentEntries = this.balanceEntriesSubject.value;
            const existingEntry = currentEntries.find(e => e.name === charge.name);

            if (existingEntry) {
                await this.updateBalanceEntryAmount(charge.name, newAmount);
            } else {
                const newEntry: BalanceEntry = {
                    id: `charge-${charge.name}-${Date.now()}`,
                    type: 'charge',
                    name: charge.name,
                    amount: newAmount,
                    currency: charge.currency,
                    timestamp: new Date(),
                    removable: charge.deductable
                };

                this.balanceEntriesSubject.next([...currentEntries, newEntry]);
            }
        }

        // Recalculate totals
        await this.recalculate();
    }

    /**
     * Restore a charge (undo adjustment)
     */
    async restoreCharge(charge: Charge): Promise<void> {
        const currentAdjustments = this.chargeAdjustmentsSubject.value;

        // Remove adjustment
        const filteredAdjustments = currentAdjustments.filter(
            adj => adj.charge.name !== charge.name
        );
        this.chargeAdjustmentsSubject.next(filteredAdjustments);

        // Restore original charge in balance
        const currentEntries = this.balanceEntriesSubject.value;
        const existingEntry = currentEntries.find(entry => entry.name === charge.name);

        if (existingEntry) {
            // Update existing entry
            await this.updateBalanceEntryAmount(charge.name, charge.amount);
        } else {
            // Add back to balance
            const newEntry: BalanceEntry = {
                id: `charge-${charge.name}-${Date.now()}`,
                type: 'charge',
                name: charge.name,
                amount: charge.amount,
                currency: charge.currency,
                timestamp: new Date(),
                removable: charge.deductable
            };
            this.balanceEntriesSubject.next([...currentEntries, newEntry]);
        }

        // Recalculate totals
        await this.recalculate();
    }

    /**
     * Add a one-time payment
     */
    async addOneTimePayment(payment: OneTimePayment): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan) return;

        const updatedPlan: PaymentPlan = {
            ...currentPlan,
            oneTimePayments: [...currentPlan.oneTimePayments, payment]
        };

        this.paymentPlanSubject.next(updatedPlan);

        // Add to balance as negative amount (reduces debt)
        const newEntry: BalanceEntry = {
            id: `otp-${Date.now()}`,
            type: 'one-time-payment',
            name: payment.name,
            amount: -payment.amount, // negative because it reduces debt
            currency: payment.currency,
            timestamp: payment.plannedDate,
            removable: true
        };

        this.balanceEntriesSubject.next([...this.balanceEntriesSubject.value, newEntry]);

        // Recalculate totals
        await this.recalculate();
    }

    /**
     * Remove a one-time payment
     */
    async removeOneTimePayment(paymentName: string): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan) return;

        const updatedPlan: PaymentPlan = {
            ...currentPlan,
            oneTimePayments: currentPlan.oneTimePayments.filter(p => p.name !== paymentName)
        };

        this.paymentPlanSubject.next(updatedPlan);

        // Remove from balance
        await this.removeBalanceEntryByName(paymentName);

        // Recalculate totals
        await this.recalculate();
    }


    /**
     * Remove a charge payment
     */
    async removeChargeEntry(entryId: string): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        this.balanceEntriesSubject.next(
            currentEntries.filter(e => e.id !== entryId)
        );

        await this.recalculate();
    }

    /**
     * Set the payback amount per month (from slider)
     */
    async setPaybackAmountPerMonth(amount: number): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan) return;

        const updatedPlan: PaymentPlan = {
            ...currentPlan,
            payBackAmountPerMonth: amount
        };

        this.paymentPlanSubject.next(updatedPlan);

        // Recalculate totals
        await this.recalculate();
    }

    async setDurationMonths(durationMonths: number): Promise<void> {
        const currentPlan = this.paymentPlanSubject.value;
        if (!currentPlan || durationMonths <= 0) return;

        const totalOwedAmount = this.paymentPlanResultSubject.value?.remainingDebt ?? 0;
        if (totalOwedAmount <= 0) return;

        const newMonthlyPaybackAmount =
            Math.ceil((totalOwedAmount / durationMonths) * 100) / 100;

        await this.setPaybackAmountPerMonth(newMonthlyPaybackAmount);
    }

    /**
     * Remove a balance entry by ID
     */
    private async removeBalanceEntry(id: string): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        this.balanceEntriesSubject.next(currentEntries.filter(entry => entry.id !== id));
    }

    /**
     * Remove a balance entry by name
     */
    private async removeBalanceEntryByName(name: string): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        this.balanceEntriesSubject.next(currentEntries.filter(entry => entry.name !== name));
    }

    /**
     * Update balance entry amount
     */
    private async updateBalanceEntryAmount(name: string, newAmount: number): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        const updatedEntries = currentEntries.map(entry => {
            if (entry.name === name) {
                return {...entry, amount: newAmount};
            }
            return entry;
        });
        this.balanceEntriesSubject.next(updatedEntries);
    }

    /**
     * Recalculate all totals and payment plan result
     */
    private async recalculate(): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const currentPlan = this.paymentPlanSubject.value;
                const dunningData = this.dunningDataSubject.value;

                if (!currentPlan || !dunningData) {
                    resolve();
                    return;
                }

                // Calculate total owed amount from balance entries
                const balanceEntries = this.balanceEntriesSubject.value;
                const totalOwedAmount = this.roundToTwoDecimals(
                    balanceEntries.reduce((sum, entry) => sum + entry.amount, 0)
                );

                // Update payment plan with total
                const updatedPlan: PaymentPlan = {
                    ...currentPlan,
                    totalOwedAmount: totalOwedAmount
                };
                this.paymentPlanSubject.next(updatedPlan);

                // Calculate monthly active contract payments
                const monthlyActiveContractPayments = this.calculateMonthlyActiveContractPayments(currentPlan);

                // Calculate duration
                const paybackAmount = currentPlan.payBackAmountPerMonth || 0;
                const durationMonths = paybackAmount > 0
                    ? Math.ceil(totalOwedAmount / paybackAmount)
                    : 0;

                // Calculate monthly total payment
                const monthlyTotalPayment = this.roundToTwoDecimals(
                    monthlyActiveContractPayments + paybackAmount
                );

                // Create payment plan result
                const result: PaymentPlanResult = {
                    monthlyActiveContractPayments: this.roundToTwoDecimals(monthlyActiveContractPayments),
                    durationMonths: durationMonths,
                    monthlyPaybackAmount: this.roundToTwoDecimals(paybackAmount),
                    monthlyTotalPayment: monthlyTotalPayment,
                    remainingDebt: this.roundToTwoDecimals(totalOwedAmount)
                };

                this.paymentPlanResultSubject.next(result);
                resolve();
            }, 0);
        });
    }

    /**
     * Calculate monthly payments for active contracts
     */
    private calculateMonthlyActiveContractPayments(paymentPlan: PaymentPlan): number {
        const dunningData = this.dunningDataSubject.value;
        if (!dunningData) return 0;

        let total = 0;

        // Add monthly price for reactivated contracts
        paymentPlan.contracts.forEach(contractActivity => {
            if (contractActivity.activity === 'reactivate') {
                const monthlyPrice = this.getCurrentMonthlyPrice(contractActivity.contract);
                total += monthlyPrice;
            }
        });

        // Add monthly price for "new" contracts (always active)
        dunningData.contracts.forEach(contract => {
            if (contract.status === 'new') {
                const monthlyPrice = this.getCurrentMonthlyPrice(contract);
                total += monthlyPrice;
            }
        });

        return total;
    }

    /**
     * Get current monthly price for a contract
     */
    private getCurrentMonthlyPrice(contract: Contract): number {
        if (!contract.monthlyPrice || contract.monthlyPrice.length === 0) {
            return 0;
        }

        // Get the most recent price (last entry in array)
        // In a real implementation, you'd compare dates
        const latestPrice = contract.monthlyPrice[contract.monthlyPrice.length - 1];
        return latestPrice.monthlyPrice;
    }

    /**
     * Round to 2 decimal places (matching BigDecimal precision)
     */
    private roundToTwoDecimals(value: number): number {
        return Math.round(value * 100) / 100;
    }

    /**
     * Get current dunning data
     */
    getDunningData(): DunningData | null {
        return this.dunningDataSubject.value;
    }

    /**
     * Get current payment plan
     */
    getPaymentPlan(): PaymentPlan | null {
        return this.paymentPlanSubject.value;
    }

    /**
     * Get current balance entries
     */
    getBalanceEntries(): BalanceEntry[] {
        return this.balanceEntriesSubject.value;
    }

    /**
     * Get current payment plan result
     */
    getPaymentPlanResult(): PaymentPlanResult | null {
        return this.paymentPlanResultSubject.value;
    }

    setSelectedContract(contract: Contract): void {
        this.selectedContractSubject.next(contract);
    }

    clearSelectedContract(): void {
        this.selectedContractSubject.next(null);
    }

    /**
     * Reset the service state
     */
    reset(): void {
        this.dunningDataSubject.next(null);
        this.paymentPlanSubject.next(null);
        this.balanceEntriesSubject.next([]);
        this.paymentPlanResultSubject.next(null);
        this.chargeAdjustmentsSubject.next([]);
        this.selectedContractSubject.next(null);
    }
}
